import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { SnCamerasAPI } from '@lib/api/services';
import { SnCameraMarker } from '@lib/cameras/map';
import {
  GoogleMap,
  GoogleMapStyle,
  GoogleMapLayer,
  GoogleMapMarker,
  GoogleMapMarkerType,
} from '@lib/geo/google/map';
import { SnLonLatLike } from '@lib/geo/helpers';
import { t } from '@lib/translation';
import { THEME } from '@lib/ui/theme';
import { SnCamera } from 'libs/api/entities/src/lib/camera';
import { SnDialogService } from 'libs/ui/dialog/src/lib/services/dialogs.service';

@Component({
  selector: 'sn-map-street-view-component',
  templateUrl: './map-street-view-component.component.html',
  styleUrls: ['./map-street-view-component.component.scss'],
})
export class MapStreetViewComponentComponent implements OnDestroy, OnInit {
  public map: GoogleMap;
  public googleMapCenter: SnLonLatLike;
  public googleMapStyle: string;

  public streetView: google.maps.StreetViewPanorama;
  public streetViewLocation: any;
  public streetViewHeading: number;

  public streetViewService: google.maps.StreetViewService;

  public cameraMarker: GoogleMapMarker;
  public locationMarker: GoogleMapMarker;

  public readonly canEdit: boolean;

  private readonly _viewLayer: GoogleMapLayer;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public camera: SnCamera,
    private readonly _cameraAPI: SnCamerasAPI,
    private readonly _dialogService: SnDialogService,
    private readonly _ability: SnAbilityService,
  ) {
    this.googleMapCenter = this.camera.geometry.coordinates;
    this._viewLayer = new GoogleMapLayer();
    this.canEdit = this._ability.can({
      subject: 'cameras/camera',
      action: 'change',
    });
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onMapInit(googleMap: GoogleMap): void {
    this.map = googleMap;
    this.map.setStyle(GoogleMapStyle.Dark);
    this.streetViewService = new google.maps.StreetViewService();
    this._viewLayer.addTo(this.map);

    this._createStreetView();
  }

  public saveCameraHeading(): void {
    const data = {
      heading: this.streetViewHeading.toFixed(),
    };
    this._dialogService
      .confirm({
        message: t('cameras.streetView.confirmSetHeading'),
        confirmButton: {
          text: t('common.confirm'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._cameraAPI.updateCamera(this.camera.id, data).subscribe()),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  public saveCameraLocation(): void {
    const data = {
      geometry: {
        coordinates: [this.streetViewLocation.lng, this.streetViewLocation.lat],
        type: 'Point',
      },
    };
    this._dialogService
      .confirm({
        message: t('cameras.streetView.confirmSetLocation'),
        confirmButton: {
          text: t('common.confirm'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._cameraAPI.updateCamera(this.camera.id, data).subscribe()),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  private _createMarker(locationCoords: any): void {
    this.cameraMarker = new SnCameraMarker(this.camera);
    this.locationMarker = new GoogleMapMarker({
      lonLat: { lon: locationCoords.lng, lat: locationCoords.lat },
      type: GoogleMapMarkerType.CIRCLE,
      style: {
        scale: 1,
        strokeColor: THEME.success,
        rotation: this.streetViewHeading,
      },
      feature: {
        geometry: null,
        type: 'Feature',
        properties: {},
      },
      icon: {
        fontSet: 'fa',
        fontIcon: 'fa-arrow-up',
        color: THEME.success,
      },
    });
    this._viewLayer.addOverlay(this.cameraMarker);
    this._viewLayer.addOverlay(this.locationMarker);
  }

  private _createStreetView(): void {
    this.streetView = new google.maps.StreetViewPanorama(
      document.getElementById('pano') as HTMLElement,
      {
        position: { lat: this.googleMapCenter[1], lng: this.googleMapCenter[0] },
        pov: {
          heading: this.camera.heading,
          pitch: 10,
        },
        disableDefaultUI: true,
        addressControl: false,
        fullscreenControl: false,
      },
    );

    this.map.setStreetView(this.streetView);

    this.streetViewLocation = this.streetView.getPosition().toJSON();
    this.streetViewHeading = this.streetView.getPov().heading;

    this.streetView.addListener('position_changed', () => {
      this.streetViewLocation = this.streetView.getPosition().toJSON();
      this._updateMarker(this.locationMarker, this.streetViewLocation, undefined);
    });

    this.streetView.addListener('pov_changed', () => {
      this.streetViewHeading = this.streetView.getPov().heading;
      this._updateMarker(this.locationMarker, this.streetViewLocation, this.streetViewHeading);
    });

    this._getPanorama();
  }

  private _getPanorama(): void {
    this.streetViewService.getPanorama(
      {
        location: { lat: this.googleMapCenter[1], lng: this.googleMapCenter[0] },
        radius: 50,
      },
      (res) => {
        this._createMarker(res.location.latLng.toJSON());
      },
    );
  }

  private _updateMarker(marker: GoogleMapMarker, markerLocation?: any, markerPov?: any): void {
    if (this._viewLayer.hasOverlay(marker)) {
      marker.setLonLat({
        lat: markerLocation.lat,
        lng: markerLocation.lng,
      });
      marker.setStyle({
        scale: 1,
        strokeColor: THEME.success,
        rotation: markerPov || marker.getStyle().rotation,
      });
    }
  }
}
